import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { brokerageAPI } from '../lib/api';
import { 
  Handshake, Loader2, RefreshCw, DollarSign,
  Users, Clock, CheckCircle, Edit2, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function AdminBrokeragePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [requestsRes, statsRes] = await Promise.all([
        brokerageAPI.adminList({ status: filter }),
        brokerageAPI.adminStats(),
      ]);
      setRequests(requestsRes.data.requests || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load brokerage data');
    }
    setLoading(false);
  };

  const handleUpdate = async (id) => {
    try {
      await brokerageAPI.adminUpdate(id, editForm);
      toast.success('Request updated');
      setEditingId(null);
      loadData();
    } catch {
      toast.error('Update failed');
    }
  };

  const startEdit = (req) => {
    setEditingId(req.id);
    setEditForm({
      status: req.status,
      assigned_broker: req.assigned_broker || '',
      owner_contacted: req.owner_contacted || false,
      owner_response: req.owner_response || '',
      current_offer: req.current_offer || '',
      final_price: req.final_price || '',
      status_message: '',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-500/20 text-amber-400',
      assigned: 'bg-blue-500/20 text-blue-400',
      negotiating: 'bg-purple-500/20 text-purple-400',
      offer_made: 'bg-cyan-500/20 text-cyan-400',
      accepted: 'bg-green-500/20 text-green-400',
      declined: 'bg-red-500/20 text-red-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-zinc-500/20 text-zinc-400',
    };
    return colors[status] || 'bg-zinc-500/20 text-zinc-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-8" data-testid="admin-brokerage-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Outfit'] flex items-center gap-3">
            <Handshake className="h-8 w-8 text-[#00E5FF]" />
            Brokerage Management
          </h1>
          <p className="text-zinc-500 mt-1">Manage domain acquisition requests</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm" className="border-zinc-700 text-zinc-300">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-zinc-500" />
              <div>
                <p className="text-zinc-500 text-xs">Total Requests</p>
                <p className="text-2xl font-bold text-white">{stats?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-zinc-500 text-xs">Active</p>
                <p className="text-2xl font-bold text-amber-400">{stats?.active || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-zinc-500 text-xs">Completed</p>
                <p className="text-2xl font-bold text-green-400">{stats?.completed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#0A0A0A] border-white/5">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-[#00E5FF]" />
              <div>
                <p className="text-zinc-500 text-xs">Commission</p>
                <p className="text-2xl font-bold text-[#00E5FF]">${stats?.total_commission?.toLocaleString() || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="mb-6 flex gap-2 flex-wrap">
        {['', 'pending', 'assigned', 'negotiating', 'offer_made', 'completed'].map((status) => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFilter(status); }}
            className={filter === status ? 'bg-[#00E5FF] text-black' : 'border-zinc-700 text-zinc-300'}
          >
            {status || 'All'}
          </Button>
        ))}
      </div>

      {/* Requests Table */}
      <Card className="bg-[#0A0A0A] border-white/5">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Domain</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Client</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Budget</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Broker</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Current Offer</th>
                  <th className="text-left py-3 px-4 text-zinc-500 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <React.Fragment key={req.id}>
                    <tr className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <p className="text-white font-medium">{req.target_domain}</p>
                        <p className="text-zinc-500 text-xs">{req.created_at?.split('T')[0]}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-zinc-300">{req.client_name}</p>
                        <p className="text-zinc-500 text-xs">{req.client_email}</p>
                      </td>
                      <td className="py-3 px-4 text-zinc-300">
                        ${req.budget_min?.toLocaleString()} - ${req.budget_max?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-zinc-400">{req.assigned_broker || '-'}</td>
                      <td className="py-3 px-4 text-[#00E5FF]">
                        {req.current_offer ? `$${parseInt(req.current_offer).toLocaleString()}` : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => startEdit(req)}
                          className="text-[#00E5FF]"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                    {editingId === req.id && (
                      <tr className="bg-zinc-900/50">
                        <td colSpan={7} className="p-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <Label className="text-zinc-400 text-xs">Status</Label>
                              <select
                                value={editForm.status}
                                onChange={(e) => setEditForm(p => ({ ...p, status: e.target.value }))}
                                className="w-full mt-1 h-10 bg-zinc-800 border border-zinc-700 rounded text-white px-3"
                              >
                                <option value="pending">Pending</option>
                                <option value="assigned">Assigned</option>
                                <option value="negotiating">Negotiating</option>
                                <option value="offer_made">Offer Made</option>
                                <option value="accepted">Accepted</option>
                                <option value="declined">Declined</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Assigned Broker</Label>
                              <Input
                                value={editForm.assigned_broker}
                                onChange={(e) => setEditForm(p => ({ ...p, assigned_broker: e.target.value }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                                placeholder="Broker name"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Current Offer ($)</Label>
                              <Input
                                type="number"
                                value={editForm.current_offer}
                                onChange={(e) => setEditForm(p => ({ ...p, current_offer: e.target.value }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Final Price ($)</Label>
                              <Input
                                type="number"
                                value={editForm.final_price}
                                onChange={(e) => setEditForm(p => ({ ...p, final_price: e.target.value }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <Label className="text-zinc-400 text-xs">Owner Response</Label>
                              <Input
                                value={editForm.owner_response}
                                onChange={(e) => setEditForm(p => ({ ...p, owner_response: e.target.value }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                                placeholder="e.g., Interested, Declined, Counter-offer"
                              />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Status Update Message</Label>
                              <Input
                                value={editForm.status_message}
                                onChange={(e) => setEditForm(p => ({ ...p, status_message: e.target.value }))}
                                className="mt-1 bg-zinc-800 border-zinc-700 text-white"
                                placeholder="Message for client (optional)"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mb-4">
                            <label className="flex items-center gap-2 text-zinc-300">
                              <input
                                type="checkbox"
                                checked={editForm.owner_contacted}
                                onChange={(e) => setEditForm(p => ({ ...p, owner_contacted: e.target.checked }))}
                                className="rounded border-zinc-700"
                              />
                              Owner Contacted
                            </label>
                          </div>
                          {req.timeline && req.timeline.length > 0 && (
                            <div className="mb-4 p-3 bg-zinc-800 rounded">
                              <p className="text-zinc-400 text-xs mb-2 flex items-center gap-2">
                                <MessageSquare className="h-3 w-3" /> Timeline
                              </p>
                              <div className="space-y-1">
                                {req.timeline.slice(-5).map((entry, i) => (
                                  <p key={i} className="text-xs text-zinc-500">
                                    <span className="text-zinc-400">{entry.timestamp?.split('T')[0]}</span> - {entry.message}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button onClick={() => handleUpdate(req.id)} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/80">
                              Save Changes
                            </Button>
                            <Button variant="outline" onClick={() => setEditingId(null)} className="border-zinc-700 text-zinc-300">
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-zinc-500">
                      No brokerage requests yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
